const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const getMonthFromString = (dateString: Date) => {
  const dateObj = new Date(dateString);
  const month = dateObj.getMonth() + 1;

  return month;
};

const filterOnlyThisYear = <T extends Record<string, unknown>>(arr: T[], year: string, dateKey: keyof T): T[] => {
  const thisYearData = arr.filter(
    (item: T) => item[dateKey] && String(item[dateKey]).split("-")[0] === year,
  );
  return thisYearData;
};

const currentYear = new Date().getFullYear().toString();

const getAllPurchaseDates = (data: { datePurchased: string; soldTotals: number }[]) =>
  data.map((item) => ({
    name: item.datePurchased,
    soldTotals: item.soldTotals,
  }));

const getAllPurchaseMonths = (data: { name: string | Date }[]) =>
  data.map((item) => getMonthFromString(item.name as Date));

const getAllPurchaseMonthsWithTotals = (data: { name: string | Date; soldTotals: number }[]) =>
  data.map((item) => ({
    name: getMonthFromString(item.name as Date),
    soldTotals: item.soldTotals,
  }));

type NumberWithCount = { name: string; count: number };

const countNumbersInArray = (arr: number[]): NumberWithCount[] => {
  const result: NumberWithCount[] = [];
  arr.forEach((num) => {
    const existingObj = result.find((obj) => obj.name === String(num));
    if (existingObj) {
      existingObj.count++;
    } else {
      result.push({ name: String(num), count: 1 });
    }
  });
  return result;
};

const blankArray = months.map((item: string) => {
  return { name: item, count: 0 };
});

const getPurchaseMonths = (data: NumberWithCount[]) =>
  data.map((item) => {
    const monthNum = getMonthFromString(item.name as unknown as Date);
    item.name = months[monthNum - 1];
    return item;
  });

type IItem = {
  name: string;
  count?: number;
  [key: string]: string | number | undefined;
};

const getUniqueListBy = <T extends IItem>(arr: T[], key: keyof IItem): T[] => {
  return Array.from(
    new Map(arr.map((item) => [item[key], item])).values(),
  ) as T[];
};

// FIX: This is currently taking the purchase year to get the data. It should be using the year
// from each item that is sold.
export const getMonthlySoldTotals = (data: { datePurchased: string; soldTotals: number }[]) => {
  const thisYearPurchases = filterOnlyThisYear(
    data,
    currentYear,
    "datePurchased",
  );
  const allPurchaseDates = getAllPurchaseDates(thisYearPurchases).filter(
    (purchase) => purchase.soldTotals !== 0,
  );
  const allPurchaseMonthsWithTotals =
    getAllPurchaseMonthsWithTotals(allPurchaseDates);

  // function groupBy(data: { name: number, soldTotals: number }[], key: keyof typeof data ) {

  type ItemType = { name: number; soldTotals: number };
  type ResType = ItemType[];

  // Group by function with proper typing
  function groupBy(data: ItemType[], key: keyof ItemType): ResType {
    const groups: { [key: string]: ItemType } = {};

    data.forEach((item: ItemType) => {
      const keyValue = String(item[key]);
      if (!groups[keyValue]) {
        groups[keyValue] = { [key]: item[key], soldTotals: 0 } as ItemType;
      }

      groups[keyValue].soldTotals += item.soldTotals;
    });

    return Object.values(groups);
  }

  const groupedData = groupBy(allPurchaseMonthsWithTotals, "name");

  const addAllMonths = (arr: { name: number; soldTotals: number }[]) => {
    const newArr: { name: string; value: number }[] = [];
    for (let i = 0; i <= 11; i++) {
      newArr.push(
        arr[i]
          ? { name: months[i], value: arr[i].soldTotals }
          : { name: months[i], value: 0 },
      );
    }
    return newArr;
  };

  const withMonthNames = addAllMonths(groupedData);
  return withMonthNames;
};

export const getPurchasesPerMonth = (data: { datePurchased: string; soldTotals?: number }[], selectedYear?: number) => {
  const targetYear = selectedYear ? selectedYear.toString() : currentYear;
  const thisYearPurchases = filterOnlyThisYear(
    data,
    targetYear,
    "datePurchased",
  );

  const allPurchaseDates = getAllPurchaseDates(
    thisYearPurchases.map(item => ({
      datePurchased: item.datePurchased,
      soldTotals: item.soldTotals ?? 0
    }))
  );
  const allPurchaseMonths = getAllPurchaseMonths(allPurchaseDates);
  const purchaseDateArr = countNumbersInArray(allPurchaseMonths);
  const purchaseMonthArr = getPurchaseMonths(purchaseDateArr);
  const monthsArray = [...blankArray, ...purchaseMonthArr];
  const purchasesPerMonth = getUniqueListBy(monthsArray, "name");

  return purchasesPerMonth;
};
